import React, { useState } from 'react';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { KeyIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline';

interface APIKey {
  id: string;
  name: string;
  key: string;
  permissions: string[];
  createdAt: Date;
  lastUsed: Date | null;
  status: 'active' | 'revoked';
}

const AdminAPIKeysPage: React.FC = () => {
  const [keys] = useState<APIKey[]>([
    {
      id: '1',
      name: 'Production API',
      key: 'pk_live_51HTCnO********',
      permissions: ['read', 'write'],
      createdAt: new Date('2024-01-01'),
      lastUsed: new Date('2024-01-15'),
      status: 'active',
    },
    {
      id: '2',
      name: 'Test Environment',
      key: 'pk_test_51HTCnO********',
      permissions: ['read'],
      createdAt: new Date('2024-01-05'),
      lastUsed: null,
      status: 'active',
    },
  ]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">API Keys</h1>
        <Button>Create API Key</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Keys</p>
                <p className="text-2xl font-bold text-gray-900">{keys.length}</p>
              </div>
              <KeyIcon className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl font-bold text-gray-900">{keys.filter(k => k.status === 'active').length}</p>
              </div>
              <KeyIcon className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Revoked</p>
                <p className="text-2xl font-bold text-gray-900">{keys.filter(k => k.status === 'revoked').length}</p>
              </div>
              <KeyIcon className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {keys.map((key) => (
              <Card key={key.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">{key.name}</h3>
                      <p className="text-sm font-mono text-gray-600 mt-1">{key.key}</p>
                      <div className="flex gap-2 mt-2">
                        {key.permissions.map((perm) => (
                          <span key={perm} className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                            {perm}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Created: {key.createdAt.toLocaleDateString()} | 
                        Last used: {key.lastUsed ? key.lastUsed.toLocaleDateString() : 'Never'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="secondary">
                        <EyeIcon className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="danger">
                        <TrashIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAPIKeysPage;
